import { Location } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationStart, Router, Event, ActivatedRoute } from '@angular/router';
import { asWithStateMemberType, clearSelectionMembers } from '@shared/decorators/member-state';
import { DataObject } from '@shared/models/data-object.model';
import { L10nTranslationService } from 'angular-l10n';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  NextStepValidatableComponent,
  Step,
  StepComponent,
  StepValidatableComponent,
  WizardActionSlot,
  WizardControl,
  WizardDef,
} from './model/wizard.model';
import { WizardDriverService, WizardState } from './wizard-driver.service';
import { WizardProjectorDirective } from './wizard-projector.directive';
import { WIZARD_CONTROLS, WIZARD_DEF_TOKEN } from './wizard.constants';

@UntilDestroy()
@Component({
  selector: 'miq-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss'],
  standalone: false,
})
export class WizardComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(WizardProjectorDirective, { static: true }) wizardProjector: WizardProjectorDirective;

  @HostBinding('class.mat-mdc-dialog-container') matContainerClass = true;

  wizardDriverService: WizardDriverService;
  stepTabs: Array<{ key: string; step: Step<StepComponent> }>;

  wizardTitle: string;
  wizardSubTitle: string;
  stepReady = false;

  firstStep: Step<StepComponent>;
  lastStep: Step<StepComponent>;

  isLast = false;
  isFirst = false;
  isCustomSteps = false;
  closeTabOnWizardClose = false;
  /** Zero-based index of the currently active step — used to detect backward navigation from tab header clicks. */
  currentStepIndex = 0;

  private currentStepName: string;
  private stepStateStore: { [key: string]: DataObject } = {};

  private componentRef: ComponentRef<StepComponent>;
  private dataOutEventSubscription: Subscription;
  private showStopButtonEventSubscription: Subscription;

  public lastButtonText: string;
  public hideBackButton: boolean;
  public showStopButton: boolean;
  public actionSlots: WizardActionSlot[] = [];
  CLOSE_TAB_QUERY_PARAM = 'closeTab';

  constructor(
    @Inject(WIZARD_DEF_TOKEN) private readonly wizardDef: WizardDef,
    @Inject(WIZARD_CONTROLS) private control: WizardControl,
    public translationService: L10nTranslationService,
    private router: Router,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.wizardTitle = this.wizardDef.title;
    this.wizardSubTitle = this.wizardDef.subTitle;
    this.isCustomSteps = !!this.wizardDef?.isCustomSteps;
    this.lastButtonText =
      this.wizardDef.lastButtonText !== undefined ? this.wizardDef.lastButtonText : 'recordsSearch.done';
    this.hideBackButton = !!this.wizardDef.hideBackButton;
    this.actionSlots = this.wizardDef.actionSlots ?? [];
    /* required by translation service*/
    this.wizardDriverService = new WizardDriverService(this.wizardDef);
    this.wizardDriverService.start();

    this.stepTabs = this.wizardDriverService.stepsKeys;

    const [firstStep] = this.wizardDriverService.steps;
    const [lastStep] = [...this.wizardDriverService.steps].reverse();
    this.firstStep = firstStep;
    this.lastStep = lastStep;

    // close on navigation events
    this.router.events
      .pipe(
        filter((event: Event) => event instanceof NavigationStart),
        tap(() => this.onNavClose()),
        take(1)
      )
      .subscribe();

    this.activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe(params => {
      this.closeTabOnWizardClose = !!params[this.CLOSE_TAB_QUERY_PARAM];
    });
  }

  ngAfterContentInit() {
    this.wizardDriverService.currentStep$
      .pipe(
        untilDestroyed(this),
        tap(currentStep => {
          // dispose current event subscription (if any) on every step change
          this.disposeSubscriptions();
          // save step state variables
          this.saveStepState();
          this.updateFirstLastFlags(currentStep);
          // Track current step index for backward-navigation detection in loadStepByIndex.
          this.currentStepIndex = this.wizardDriverService.steps.findIndex(
            s => s.stepNumber === currentStep.stepNumber
          );
        }),
        withLatestFrom(this.wizardDriverService.data$)
      )
      .subscribe(([currentStep, data]) => {
        this.loadStep({ currentStep, data });
        // Trigger change detection after step loads
        this.changeDetectorRef.detectChanges();
      });

    // close portal if finished
    this.wizardDriverService.finished$
      .pipe(
        take(1),
        tap(() => this.disposeSubscriptions()),
        withLatestFrom(this.wizardDriverService.data$),
        map(([, data]) => data)
      )
      .subscribe(data => this.control.close(data));
  }

  ngOnDestroy(): void {
    /* required by translation service*/
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }
    this.wizardDriverService.destroy();
    this.wizardProjector.ngOnDestroy();
    this.wizardProjector = null;
    this.stepStateStore = null;
  }

  /**
   * Updates first and last flag based on current step stepNumber
   * @param currentStep
   */
  private updateFirstLastFlags(currentStep: Step<StepComponent>): void {
    this.isFirst = currentStep.stepNumber === this.firstStep.stepNumber;
    this.isLast = currentStep.stepNumber === this.lastStep.stepNumber;
  }

  /**
   * Loads state.step component populating with current data accumulation
   * @param state {WizardState}
   */
  private loadStep(state: WizardState): void {
    this.currentStepName = state.currentStep.name;
    const { component } = state.currentStep;
    // const componentFactory = this.partyFactoryResolver.resolveComponentFactory(component.type);

    const viewContainerRef = this.wizardProjector.viewContainerRef;
    viewContainerRef.clear();

    const componentRef = viewContainerRef.createComponent(component.type);

    if (this.stepStateStore[this.currentStepName]) {
      Object.assign(componentRef.instance, this.stepStateStore[this.currentStepName]);
    }

    componentRef.instance.dataIn = { ...component.args.dataIn, ...state.data };
    componentRef.instance.options = { ...component.args.options };

    // Assign componentRef before validateStep so valid() is called on the incoming step component
    this.componentRef = componentRef;

    // Trigger change detection after component creation and data binding
    this.changeDetectorRef.markForCheck();

    this.validateStep(state.currentStep, state.data);
    if (componentRef.instance.dataOut) {
      // StepSummary does not have emitters
      this.dataOutEventSubscription = componentRef.instance.dataOut
        .pipe(
          tap(changes => this.wizardDriverService.updateData(changes)),
          switchMap(() => this.wizardDriverService.data$)
        )
        .subscribe(data => this.validateStep(state.currentStep, data));
    }

    if (componentRef.instance.showStopButton) {
      this.showStopButtonEventSubscription = componentRef.instance.showStopButton.subscribe(
        flag => (this.showStopButton = flag)
      );
    }
  }

  /**
   * Loads selected tab when navigating via step header click.
   * - Backward navigation (clicking a previous step) is always allowed.
   * - Forward navigation is gated by stepReady so the user can't skip past an incomplete step.
   */
  loadStepByIndex(stepIndex: number): void {
    const isBackward = stepIndex < this.currentStepIndex;
    if (isBackward || this.stepReady) {
      this.showStopButton = false;
      const nextStep = this.wizardDriverService.steps[stepIndex];
      this.wizardDriverService.loadStep(nextStep);
    }
  }

  /**
   * Verifies every targetData flagged as required to be populated
   * @param step currentStep
   * @param data data$ snapshot
   */
  private validateStep(step: Step<StepComponent>, data: DataObject): void {
    const { targetData } = step;

    const dataValid = Object.keys(targetData)
      // validate only required
      .filter(field => !!targetData[field])
      .every(field => !!data[field]);

    let componentValid = true;
    if (this.componentRef) {
      const validatableStepComponent = this.componentRef.instance as StepValidatableComponent;
      componentValid = typeof validatableStepComponent.valid === 'function' ? validatableStepComponent.valid() : true;
    }
    setTimeout(() => {
      this.stepReady = dataValid && componentValid;
    }, 10);
  }

  /**
   * unsubscribe current event listener if exists
   */
  private disposeSubscriptions() {
    if (this.dataOutEventSubscription) {
      this.dataOutEventSubscription.unsubscribe();
    }
    if (this.showStopButtonEventSubscription) {
      this.showStopButtonEventSubscription.unsubscribe();
    }
  }

  /**
   * drives to next step
   */
  next(): void {
    let componentValid = true;
    if (this.componentRef) {
      const validatableStepComponent = this.componentRef.instance as NextStepValidatableComponent;
      componentValid =
        typeof validatableStepComponent.validateOnNext === 'function'
          ? validatableStepComponent.validateOnNext()
          : true;
    }
    if (componentValid) {
      this.wizardDriverService.next();
    }
  }

  /**
   * drives to previous step
   */
  prev(): void {
    this.showStopButton = false;
    this.wizardDriverService.prev();
  }

  /**
   * Clears a slot's selection from wizard data and optionally navigates back to its step.
   *
   * For every affected step (target and subsequent), only @SelectionMember fields are
   * reset in the stepStateStore — filters and other state are preserved so the user
   * does not lose their search context when re-selecting a record.
   */
  clearSlot(slot: WizardActionSlot): void {
    const cleared = slot.clearKeys.reduce<Record<string, null>>((acc, key) => {
      acc[key] = null;
      return acc;
    }, {});
    this.wizardDriverService.updateData(cleared);

    if (slot.clearStep) {
      const stepsKeys = this.wizardDriverService.stepsKeys;
      const targetIndex = stepsKeys.findIndex(s => s.key === slot.clearStep);
      if (targetIndex !== -1) {
        stepsKeys.slice(targetIndex).forEach(({ step }) => {
          // Resolve the component prototype so SelectionMemberSymbol metadata is available.
          const proto = step.component.type?.prototype;
          const stored = this.stepStateStore[step.name];
          if (stored) {
            clearSelectionMembers(stored, proto);
          }
          // Also clear the live component instance so that saveStepState() — called during the
          // upcoming step transition — persists the cleared state rather than restoring it.
          if (this.componentRef && step.name === this.currentStepName) {
            clearSelectionMembers(this.componentRef.instance, proto);
          }
        });
        this.wizardDriverService.loadStep(stepsKeys[targetIndex].step);
      }
    }
  }

  /**
   * Preserves a copy of state members of a component to be loaded next time component
   * is loaded in screen
   * @see @shared/decorators/member-state.ts decorators
   */
  private saveStepState(): void {
    const stateWithMembersComponent = asWithStateMemberType(this.componentRef);
    if (!!stateWithMembersComponent) {
      const stepState = stateWithMembersComponent.StateMembers();
      this.stepStateStore[this.currentStepName] = stepState;
    }
  }

  onNavClose() {
    this.control.close();
  }

  onClose(): void {
    this.control.close();
    if (this.closeTabOnWizardClose && globalThis.close) {
      globalThis.close();
    }
    if (this.wizardDef.goBackOnclose) {
      this.location.back();
    }
  }
}
